import React, { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery } from "react-query";
import {
  listAudienceCluster,
  saveClusterAudience,
} from "../../services/insights";
import { Dialog } from "../../components/Dialog";
import { ButtonProps } from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import { Autocomplete } from "../../components/Autocomplete";
import TextField from "@mui/material/TextField";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { Cluster } from "../../types/insights";
import { useToast } from "../../hooks/useToast";
import { queryClient } from "../../services/queryClient";
import Skeleton from "@mui/material/Skeleton";

type DialogFilterSaveProps = {
  loadingFilter?: boolean;
  open: boolean;
  onClose: () => void;
  onChange: (key: string, value: any) => void;
  onSave: () => void;
};

const filter = createFilterOptions<Cluster>();

export default function DialogFilterSave({
  loadingFilter,
  open,
  onClose,
  onChange,
  onSave,
}: DialogFilterSaveProps) {
  const toast = useToast();
  const [cluster, setCluster] = useState(null);

  const clusters = useQuery("listAudienceCluster", listAudienceCluster);

  const newCluster = useMutation(
    async (param1: any) => {
      const response = await saveClusterAudience(param1);
      return response;
    },
    {
      onSuccess: async (response) => {
        toast.show("Cluster criado com sucesso", { severity: "success" });
        queryClient.invalidateQueries(["listAudienceCluster"]);

        onChange("clusterName", response.data);
        setCluster(response.data);
      },
      onError: () => {
        toast.show("Erro ao criar cluster!", {
          severity: "error",
        });
      },
    }
  );

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSaveCluster = useCallback(
    async (cluster) => {
      await newCluster.mutateAsync(cluster);
    },
    [newCluster]
  );

  const actions: ButtonProps[] = useMemo(() => {
    return [
      {
        disabled: loadingFilter,
        children: "Fechar",
        onClick: handleClose,
      },
      {
        disabled: loadingFilter,
        children: loadingFilter ? "Enviando solicitação" : "Criar",
        variant: "contained",
        onClick: onSave,
      },
    ];
  }, [handleClose, onSave, loadingFilter]);

  const handleOptionLabel = useCallback((item) => {
    // Value selected with enter, right from the input
    if (typeof item === "string") {
      return item;
    }
    // Add "xxx" option created dynamically
    if (item.inputValue) {
      return item.inputValue;
    }
    // Regular option
    return item.NOME;
  }, []);

  const handleChange = useCallback(
    (newValue) => {
      if (typeof newValue === "string") {
        // CRIA UM CLUSTER
        handleSaveCluster(newValue);
      } else if (newValue && newValue.inputValue) {
        // CRIA UM CLUSTER
        handleSaveCluster(newValue.inputValue);
        setCluster({
          NOME: newValue.inputValue,
        });
      } else {
        // SELECIONA UM CLUSTER JÁ EXISTENTE
        onChange("clusterName", newValue);
        setCluster(newValue);
      }
    },
    [onChange, handleSaveCluster]
  );

  const handleFilter = useCallback((options, params) => {
    const filtered = filter(options, params);

    const { inputValue } = params;
    // Suggest the creation of a new value
    const isExisting = options.some((option) => inputValue === option.title);
    if (inputValue !== "" && !isExisting) {
      filtered.push({
        //@ts-ignore
        inputValue,
        NOME: `Add "${inputValue}"`,
      });
    }

    return filtered;
  }, []);

  return (
    <Dialog
      title="Salvar audiência"
      open={open}
      fullWidth
      maxWidth="sm"
      actions={actions}
    >
      <Stack spacing={4} my={2}>
        {newCluster.isLoading ? (
          <Skeleton height={60} variant="text" />
        ) : (
          <Autocomplete
            selectOnFocus
            clearOnBlur
            handleHomeEndKeys
            freeSolo
            fullWidth
            loading={clusters.isLoading}
            size="small"
            label="Clusters"
            options={clusters.data}
            value={cluster}
            getOptionLabel={handleOptionLabel}
            onChange={(_, newValue) => handleChange(newValue)}
            filterOptions={handleFilter}
          />
        )}

        <TextField
          size="small"
          id="name"
          variant="outlined"
          label="Nome da audiência"
          fullWidth
          onChange={({ target: { value } }) => onChange("filterName", value)}
        />
      </Stack>
    </Dialog>
  );
}
